import clsx from "clsx";
import { getClassForStatus } from "../../../constant/status";
import { resolveJourneyColors } from "../../../constant/journeyStatus";
import { StyledJourneyStatusTag, StyledStatusTag, StyledTagSpan } from "./Tag.styles";

/**
 * StatusTag component – renders a pill-style tag with a leading status-dot.
 *
 * Params
 * - className (string): Raw status string (e.g. "Accepted", "Cancelled"). It is
 *   translated by `getClassForStatus` to a color-variant class ("green",
 *   "red", etc.) that drives the visual styling in `StyledStatusTag`.
 * - children (ReactNode): The label text (or nested elements) to display
 *   inside the tag.
 * - ...props (object): Any additional props are forwarded to
 *   `StyledStatusTag` (e.g., `style`, `onClick`, `data-*` attributes).
 *
 * Returns
 * - (JSX.Element): A `<span>` styled by `StyledStatusTag`, with the correct
 *   color variant applied and the original `children` rendered inside.
 */
export const StatusTag = ({ className, children, palette, ...props }) => {
  if (palette === "journey") {
    const colors = resolveJourneyColors(className);
    if (colors) {
      return (
        <StyledJourneyStatusTag
          {...props}
          $text={colors.text}
          $bg={colors.bg}
          $dot={colors.dot}
        >
          {children}
        </StyledJourneyStatusTag>
      );
    }
  }

  return (
    <StyledStatusTag {...props} className={clsx(getClassForStatus(className))}>
      {children}
    </StyledStatusTag>
  );
};

/**
 * \Tag component – renders a pill-style tag.
 *
 * Params
 * - ...props (object): Any additional props are forwarded to
 *   `StyledStatusTag` (e.g., `style`, `onClick`, `data-*` attributes).
 *
 * Returns
 * - (JSX.Element): A `<span>` styled by `StyledTagSpan`, with the correct
 *   color variant applied and the original `children` rendered inside.
 */
export const Tag = ({ children, ...props }) => <StyledTagSpan {...props}>{children}</StyledTagSpan>;
